/**
 * Satellite catalogue modal, ported from loader-catalog.js: search CelesTrak by
 * name (400 ms debounce, 2 characters or more), load a group (2 h cache), a
 * virtualised list of 40 px rows with "Add" per row and "Add all shown", and
 * the satellites currently on the globe with per-tag removal.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'radix-ui';
import { Download, Plus, Satellite, Search, X } from 'lucide-react';
import { IconButton, TextButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { CELESTRAK } from '../constants';
import { catalogMissionId, catalogRow, ommNoradId } from '../catalog/celestrak';
import {
  MAX_ADD_AT_ONCE,
  MIN_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
  selectCatalogMissions,
  useCatalog,
} from '../catalog/useCatalog';
import { useLoader } from '../store';

/** Row height in px (as the original). */
export const ROW_HEIGHT = 40;
/** Rows rendered above and below the viewport (as the original). */
const BUFFER_ROWS = 10;
/** Fixed viewport height of the list. */
const LIST_HEIGHT = 400;

const inputClass =
  'rounded-control border border-hairline bg-inset px-2.5 py-1.5 text-xs text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none';

export function CatalogModal() {
  const open = useCatalog((s) => s.open);
  const setOpen = useCatalog((s) => s.setOpen);
  const query = useCatalog((s) => s.query);
  const setQuery = useCatalog((s) => s.setQuery);
  const group = useCatalog((s) => s.group);
  const setGroup = useCatalog((s) => s.setGroup);
  const status = useCatalog((s) => s.status);
  const statusText = useCatalog((s) => s.statusText);
  const results = useCatalog((s) => s.results);
  const loadedAtMs = useCatalog((s) => s.loadedAtMs);
  const search = useCatalog((s) => s.search);
  const loadGroup = useCatalog((s) => s.loadGroup);
  const addToGlobe = useCatalog((s) => s.addToGlobe);
  const removeFromGlobe = useCatalog((s) => s.removeFromGlobe);
  const removeAll = useCatalog((s) => s.removeAll);
  const missions = useLoader((s) => s.dataset?.missions);

  const [scrollTop, setScrollTop] = useState(0);

  // Debounced search: queries of two or more characters run 400 ms after the last keystroke.
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return;
    const timer = window.setTimeout(() => void search(trimmed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, search]);

  const active = useMemo(() => selectCatalogMissions(missions), [missions]);
  const activeIds = useMemo(() => new Set(active.map((m) => m.id)), [active]);

  const total = results.length;
  const firstVisible = Math.floor(scrollTop / ROW_HEIGHT);
  const visibleCount = Math.ceil(LIST_HEIGHT / ROW_HEIGHT);
  const startIdx = Math.max(0, firstVisible - BUFFER_ROWS);
  const endIdx = Math.min(total, firstVisible + visibleCount + BUFFER_ROWS);
  const rows = [];
  for (let i = startIdx; i < endIdx; i++) {
    const omm = results[i];
    const row = catalogRow(omm, loadedAtMs);
    rows.push({ index: i, omm, row, onGlobe: activeIds.has(catalogMissionId(row.noradId)) });
  }
  const notOnGlobe = results.filter((omm) => !activeIds.has(catalogMissionId(ommNoradId(omm))));

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 flex w-[min(880px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none"
        >
          <div className="flex h-11 shrink-0 items-center justify-between border-b border-hairline px-4">
            <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-ink">
              <Satellite className="h-4 w-4 text-accent" />
              Satellite catalogue
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton label="Close (Esc)">
                <X className="h-4 w-4" />
              </IconButton>
            </Dialog.Close>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-3">
            <label className="relative min-w-[260px] flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <input
                type="search"
                aria-label="Search by name"
                placeholder="Search by name (e.g. ISS, GOES, Starlink)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className={cn(inputClass, 'w-full pl-8')}
              />
            </label>
            <select
              aria-label="Catalogue group"
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              className={inputClass}
            >
              <option value="">Select group</option>
              {Object.entries(CELESTRAK.GROUPS).map(([label, key]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <TextButton
              disabled={!group || status === 'loading'}
              onClick={() => void loadGroup(group)}
              className="inline-flex items-center gap-1.5 border border-hairline"
            >
              <Download className="h-3.5 w-3.5" />
              Load
            </TextButton>
          </div>

          <div className="flex items-center justify-between gap-2 px-4 py-2">
            <span className={cn('truncate', status === 'error' ? 'text-warning' : 'text-ink-2')} role="status">
              {statusText}
            </span>
            <TextButton
              disabled={notOnGlobe.length === 0}
              onClick={() => addToGlobe(notOnGlobe)}
              className="inline-flex shrink-0 items-center gap-1.5 bg-accent-soft text-accent"
              title={
                notOnGlobe.length > MAX_ADD_AT_ONCE
                  ? `Adds the first ${MAX_ADD_AT_ONCE} of ${notOnGlobe.length}`
                  : 'Add every listed satellite to the globe'
              }
            >
              <Plus className="h-3.5 w-3.5" />
              Add all shown ({Math.min(notOnGlobe.length, MAX_ADD_AT_ONCE)})
            </TextButton>
          </div>

          <div
            className="relative mx-4 overflow-y-auto rounded-control border border-hairline bg-inset scrollbar-thin"
            style={{ height: LIST_HEIGHT }}
            onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            role="list"
            aria-label="Catalogue results"
          >
            {total === 0 ? (
              <div className="flex h-full items-center justify-center text-ink-3">
                {status === 'loading' ? 'Loading' : 'Search by name or load a group to list satellites.'}
              </div>
            ) : (
              <div className="relative" style={{ height: total * ROW_HEIGHT }}>
                {rows.map(({ index, omm, row, onGlobe }) => (
                  <div
                    key={row.noradId || index}
                    role="listitem"
                    className={cn(
                      'absolute left-0 right-0 flex items-center gap-3 border-b border-hairline/60 px-3',
                      onGlobe && 'bg-accent-soft/40',
                    )}
                    style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
                  >
                    <span className="min-w-0 flex-1 truncate text-ink" title={row.name}>
                      {row.name}
                    </span>
                    <span className="w-16 shrink-0 font-mono text-ink-3">#{row.noradId}</span>
                    <span className="w-28 shrink-0 text-ink-2">
                      {row.orbitType} {row.altKm === null ? '--' : `${row.altKm} km`}
                    </span>
                    <span className="w-14 shrink-0 text-ink-2">{row.inclinationDeg.toFixed(1)} deg</span>
                    <span className="w-10 shrink-0 text-right font-mono text-ink-3" title="TLE epoch age">
                      {row.epochAge}
                    </span>
                    {onGlobe ? (
                      <span className="w-20 shrink-0 text-right text-[11px] font-medium text-accent">On globe</span>
                    ) : (
                      <TextButton onClick={() => addToGlobe([omm])} className="w-20 shrink-0 border border-hairline">
                        Add
                      </TextButton>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 px-4 py-3">
            <span className="text-ink-2">
              On globe: <strong className="text-ink">{active.length}</strong>
            </span>
            <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
              {active.map((m) => (
                <span
                  key={m.id}
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] text-ink"
                  style={{ borderColor: m.color ?? 'var(--color-hairline-strong)' }}
                >
                  {m.shortName}
                  <button
                    type="button"
                    aria-label={`Remove ${m.name} from the globe`}
                    onClick={() => removeFromGlobe([m.id])}
                    className="text-ink-3 hover:text-ink"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <TextButton disabled={active.length === 0} onClick={removeAll} className="shrink-0 text-warning">
              Remove all
            </TextButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
